import React, { useState, useEffect, useRef, useMemo } from 'react';
import { array, bool, func, number, object, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import { seedReservationTimer } from '../../../util/reservationTimer';
import classNames from 'classnames';
import moment from 'moment';
import { FormattedMessage, intlShape, injectIntl, useIntl } from '../../../util/reactIntl';
import { required, bookingDatesRequired, composeValidators } from '../../../util/validators';
import {
  START_DATE,
  END_DATE,
  getStartOf,
  addTime,
  isSameDay,
  isDateSameOrAfter,
  isInRange,
  timeOfDayFromLocalToTimeZone,
  timeOfDayFromTimeZoneToLocal,
  monthIdString,
  initialVisibleMonth,
} from '../../../util/dates';
import { LINE_ITEM_DAY, LINE_ITEM_NIGHT, TIME_SLOT_TIME, propTypes } from '../../../util/types';
import { BOOKING_PROCESS_NAME } from '../../../transactions/transaction';
import {
  createPeoplePriceArray,
  getAvailableSeatsWithPrices,
} from '../../../util/priceCalculation';
import { getSeasonalRangeForStart, getSeasonalPriceForSeats } from '../../../util/seasonalPricing';
import { formatMoney } from '../../../util/currency';
import { types as sdkTypes } from '../../../util/sdkLoader';

import { Form, IconArrowHead, PrimaryButton, FieldDateRangeInput } from '../../../components';

import EstimatedCustomerBreakdownMaybe from '../EstimatedCustomerBreakdownMaybe';

import css from './BookingDatesForm.module.css';
import Notification from '../../notification-popup/notification';
import { extractDaysAndNights } from '../../../util/listingPages.util';

const { Money } = sdkTypes;

const TODAY = new Date();

const nextMonthFn = (currentMoment, timeZone, offset = 1) =>
  getStartOf(currentMoment, 'month', timeZone, offset, 'months');
const prevMonthFn = (currentMoment, timeZone, offset = 1) =>
  getStartOf(currentMoment, 'month', timeZone, -1 * offset, 'months');
const endOfRange = (date, dayCountAvailableForBooking, timeZone) =>
  getStartOf(date, 'day', timeZone, dayCountAvailableForBooking - 1, 'days');

/**
 * Return a boolean indicating if given date can be found in an array
 * of tile slots (start dates).
 */
const timeSlotsContain = (timeSlots, date, timeZone) => {
  const foundIndex = timeSlots.findIndex(slot =>
    isInRange(date, slot.attributes.start, slot.attributes.end, 'hour', timeZone)
  );
  return foundIndex > -1;
};

const pickMonthlyTimeSlots = (monthlyTimeSlots, date, timeZone) => {
  const monthId = monthIdString(date, timeZone);
  const timeSlots = monthlyTimeSlots?.[monthId]?.timeSlots || [];

  return timeSlots;
};

/**
 * Find first blocked date between two dates.
 * If none is found, null is returned.
 *
 * @param {Object} monthlyTimeSlots propTypes.timeSlot objects
 * @param {Moment} startDate start date, exclusive
 * @param {Moment} endDate end date, exclusive
 * @param {String} timeZone time zone id
 */
const firstBlockedBetween = (monthlyTimeSlots, startDate, endDate, timeZone) => {
  const firstDate = getStartOf(startDate, 'day', timeZone, 1, 'days');
  if (isSameDay(firstDate, endDate, timeZone)) {
    return null;
  }

  const timeSlots = pickMonthlyTimeSlots(monthlyTimeSlots, firstDate, timeZone);
  const contains = timeSlotsContain(timeSlots, firstDate, timeZone);
  return contains ? firstBlockedBetween(monthlyTimeSlots, firstDate, endDate, timeZone) : firstDate;
};

/**
 * Find last blocked date between two dates.
 * If none is found, null is returned.
 *
 * @param {Array} timeSlots propTypes.timeSlot objects
 * @param {Moment} startDate start date, exclusive
 * @param {Moment} endDate end date, exclusive
 * @param {String} timeZone time zone id
 */
const lastBlockedBetween = (monthlyTimeSlots, startDate, endDate, timeZone) => {
  const previousDate = getStartOf(endDate, 'day', timeZone, -1, 'days');
  if (isSameDay(previousDate, startDate, timeZone)) {
    return null;
  }

  const timeSlots = pickMonthlyTimeSlots(monthlyTimeSlots, previousDate, timeZone);
  return timeSlotsContain(timeSlots, previousDate, timeZone)
    ? lastBlockedBetween(monthlyTimeSlots, startDate, previousDate, timeZone)
    : previousDate;
};

/**
 * Check if a blocked date can be found between two dates.
 *
 * @param {Object} timeSlots propTypes.timeSlot objects
 * @param {String} timeZone time zone id
 * @param {Moment} startDate start date, exclusive
 * @param {Moment} endDate end date, exclusive
 */
const isBlockedBetween = (monthlyTimeSlots, timeZone) => (startDate, endDate) => {
  const startInListingTZ = getStartOf(
    timeOfDayFromLocalToTimeZone(startDate, timeZone),
    'day',
    timeZone
  );
  const endInListingTZ = getStartOf(
    timeOfDayFromLocalToTimeZone(endDate, timeZone),
    'day',
    timeZone
  );
  return !!firstBlockedBetween(monthlyTimeSlots, startInListingTZ, endInListingTZ, timeZone);
};

const isStartDateSelected = (timeSlots, startDate, endDate, focusedInput) => {
  return (
    timeSlots && startDate && (!endDate || focusedInput === END_DATE) && focusedInput !== START_DATE
  );
};

const endDateToPickerDate = (unitType, endDate, timeZone) => {
  const isValid = endDate instanceof Date;
  const isDaily = unitType === LINE_ITEM_DAY;

  if (!isValid) {
    return null;
  } else if (isDaily) {
    // API end dates are exlusive, so we need to shift them with daily
    // booking.
    return getStartOf(endDate, 'day', timeZone, -1, 'days');
  } else {
    return endDate;
  }
};

// Fast numeric key for date comparison - avoids moment.format() entirely
const getDateKey = date => {
  if (!date) return 0;
  const d = date instanceof Date ? date : new Date(date);
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
};

/**
 * Returns an isOutsideRange function that can be passed to
 * a react-dates DateRangePicker component.
 *
 * PERFORMANCE OPTIMIZED: Uses numeric keys for ultra-fast comparisons.
 *
 * CRITICAL FIX: Removed expensive firstBlockedBetween() and lastBlockedBetween()
 * calls which were recursively iterating through 365 days.
 * Now uses simple date range check - blocked days are handled by isDayBlocked.
 */
const isOutsideRangeFn = (
  monthlyTimeSlots,
  startDate,
  endDate,
  lineItemUnitType,
  dayCountAvailableForBooking,
  timeZone
) => focusedInput => {
  // Pre-compute boundaries ONCE using fast numeric keys
  const now = new Date();
  const todayKey = now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate();

  const endOfRange = new Date(now);
  endOfRange.setDate(endOfRange.getDate() + dayCountAvailableForBooking - 1);
  const endOfRangeKey =
    endOfRange.getFullYear() * 10000 + (endOfRange.getMonth() + 1) * 100 + endOfRange.getDate();

  const startDateKey = startDate ? getDateKey(startDate) : null;

  // If start date is selected, limit end date range
  if (startDate && !endDate && focusedInput === END_DATE) {
    return day => {
      const dayKey = getDayKey(day);
      // End date must be after start date and within range
      return dayKey < startDateKey || dayKey > endOfRangeKey;
    };
  }

  // Standard case: ultra-fast numeric comparison (just check if within bookable range)
  return day => {
    const dayKey = getDayKey(day);
    return dayKey < todayKey || dayKey > endOfRangeKey;
  };
};

// Checks if time slot (propTypes.timeSlot) start time equals a day (moment)
const timeSlotEqualsDay = (timeSlot, day, timeZone) => {
  const startDay = moment(timeSlot.attributes.start).format('YYYY-MM-DD');
  const comparisonDay = moment(day).format('YYYY-MM-DD');
  return startDay === comparisonDay;
};

// PERFORMANCE FIX: Pre-compute available days as a Set for O(1) lookups
// Uses numeric keys (year*10000 + month*100 + day) for ultra-fast comparison
// This avoids expensive moment.format() calls which were causing 4-5 second delays on mobile
let availableDaysCache = null;
let availableDaysCacheKey = null;

// Ultra-fast day key generation - avoids moment.format() entirely
const getDayKey = momentOrDate => {
  if (!momentOrDate) return 0;
  // Handle both moment objects and Date objects
  const d = momentOrDate._d || momentOrDate;
  if (d instanceof Date) {
    return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
  }
  // Fallback for moment
  return momentOrDate.year() * 10000 + (momentOrDate.month() + 1) * 100 + momentOrDate.date();
};

const getAvailableDaysSet = monthlyTimeSlots => {
  if (!monthlyTimeSlots) return new Set();

  // Create cache key that includes both month keys AND the number of slots per month
  // This ensures cache invalidates when slot content changes
  let cacheKey = '';
  const sortedKeys = Object.keys(monthlyTimeSlots).sort();
  sortedKeys.forEach(key => {
    const slotCount = monthlyTimeSlots[key]?.timeSlots?.length || 0;
    cacheKey += `${key}:${slotCount},`;
  });

  // Return cached set if monthlyTimeSlots hasn't changed
  if (cacheKey === availableDaysCacheKey && availableDaysCache) {
    return availableDaysCache;
  }

  // Build set of available days using numeric keys for fast lookup
  const availableDays = new Set();

  Object.values(monthlyTimeSlots).forEach(monthData => {
    if (monthData?.timeSlots) {
      monthData.timeSlots.forEach(slot => {
        if (slot?.attributes?.start) {
          // Use numeric key instead of string - much faster
          const d = new Date(slot.attributes.start);
          const key = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
          availableDays.add(key);
        }
      });
    }
  });

  availableDaysCacheKey = cacheKey;
  availableDaysCache = availableDays;

  return availableDays;
};

const dayMapping = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 0,
};

let bookableDaysCache = null;
let bookableDaysCacheKey = null;
let bookableDaysCacheSet = null;

/**
 * Days (YYYYMMDD, ascending) a booking could actually start on, derived from the time slots
 * fetched so far. The empty-month calendar overlay uses this to name the next bookable date, so
 * it has to apply the same rules the picker itself does: past days and days beyond the booking
 * window are out, and a listing with a fixed starting weekday only opens on that weekday.
 *
 * Days that haven't been fetched yet are simply absent - the overlay treats that as "not known"
 * and shows its neutral copy rather than naming the wrong date.
 */
const getBookableDays = (monthlyTimeSlots, dayCountAvailableForBooking, fixedStartingDay) => {
  const availableDays = getAvailableDaysSet(monthlyTimeSlots);
  if (availableDays.size === 0) {
    return [];
  }

  const requiredDayOfWeek =
    fixedStartingDay && fixedStartingDay !== 'any' ? dayMapping[fixedStartingDay] : null;

  const now = new Date();
  const todayKey = getDayKey(now);
  const lastBookableDay = new Date(now);
  lastBookableDay.setDate(lastBookableDay.getDate() + dayCountAvailableForBooking - 1);
  const endOfRangeKey = getDayKey(lastBookableDay);

  // getAvailableDaysSet hands back the same Set instance while the slots are unchanged, so it
  // doubles as the cache key here - only the day-independent inputs need adding to it.
  const cacheKey = `${todayKey}:${endOfRangeKey}:${requiredDayOfWeek}`;
  if (bookableDaysCacheKey === cacheKey && bookableDaysCacheSet === availableDays) {
    return bookableDaysCache;
  }

  const bookableDays = [];
  availableDays.forEach(dayKey => {
    if (dayKey < todayKey || dayKey > endOfRangeKey) {
      return;
    }
    if (requiredDayOfWeek !== null) {
      const year = Math.floor(dayKey / 10000);
      const month = Math.floor(dayKey / 100) % 100;
      if (new Date(year, month - 1, dayKey % 100).getDay() !== requiredDayOfWeek) {
        return;
      }
    }
    bookableDays.push(dayKey);
  });

  bookableDaysCacheKey = cacheKey;
  bookableDaysCacheSet = availableDays;
  bookableDaysCache = bookableDays.sort((a, b) => a - b);

  return bookableDaysCache;
};

/**
 * Returns an isDayBlocked function that can be passed to
 * a react-dates DateRangePicker component.
 *
 * PERFORMANCE OPTIMIZED: Uses pre-computed Set of available days
 * instead of calculating for each day individually.
 *
 * CRITICAL FIX: Removed expensive firstBlockedBetween() call which was
 * iterating through 365 days recursively, causing 4-5 second delays on mobile.
 *
 * AVAILABILITY FIX: Properly handles the case when slots are loaded but empty
 * (all days should be blocked) vs when slots haven't loaded yet (show neutral calendar).
 */
const isDayBlockedFn = (
  monthlyTimeSlots,
  startDate,
  endDate,
  unitType,
  dayCountAvailableForBooking,
  timeZone,
  fixedStartingDay
) => focusedInput => {
  // Pre-compute available days ONCE (O(n) where n = number of time slots)
  // Then each day check is O(1) instead of O(n)
  const availableDays = getAvailableDaysSet(monthlyTimeSlots);

  const requiredDayOfWeek =
    fixedStartingDay && fixedStartingDay !== 'any' ? dayMapping[fixedStartingDay] : null;

  // CRITICAL: Distinguish between "data not loaded" vs "data loaded but empty"
  // - no month has answered yet: Data hasn't loaded -> show neutral calendar. A month is added
  //   to monthlyTimeSlots the moment its request goes out, so counting keys would call an
  //   in-flight fetch "loaded" and blank out the whole calendar while it is running.
  // - every month answered with no slots: Data loaded but no availability -> block ALL days
  // - some month has slots: Use the Set for O(1) lookups
  const dataNotLoadedYet =
    !monthlyTimeSlots ||
    !Object.values(monthlyTimeSlots).some(monthData => Array.isArray(monthData?.timeSlots));
  const hasAvailableSlots = availableDays.size > 0;

  // Main path: O(1) lookup in pre-computed Set
  return day => {
    // Check fixed starting day constraint first (very fast)
    if (requiredDayOfWeek !== null && day.day() !== requiredDayOfWeek) {
      return true; // blocked
    }

    // If data hasn't started loading yet, don't block - show neutral calendar during initial load
    if (dataNotLoadedYet) {
      return false;
    }

    // CRITICAL FIX: If data has loaded but there are NO available slots,
    // ALL days should be BLOCKED (return true)
    if (!hasAvailableSlots) {
      return true; // blocked - no availability
    }

    // Ultra-fast O(1) lookup using numeric key
    const dayKey = getDayKey(day);
    return !availableDays.has(dayKey);
  };
};

const fetchMonthData = (
  date,
  listingId,
  dayCountAvailableForBooking,
  timeZone,
  onFetchTimeSlots
) => {
  const endOfRangeDate = endOfRange(TODAY, dayCountAvailableForBooking, timeZone);

  // Don't fetch timeSlots for past months or too far in the future
  if (isInRange(date, TODAY, endOfRangeDate)) {
    // Use "today", if the first day of given month is in the past
    const start = isDateSameOrAfter(TODAY, date) ? TODAY : date;

    // Use endOfRangeDate, if the first day of the next month is too far in the future
    const nextMonthDate = nextMonthFn(date, timeZone);
    const end = isDateSameOrAfter(nextMonthDate, endOfRangeDate)
      ? getStartOf(endOfRangeDate, 'day', timeZone)
      : nextMonthDate;

    // Fetch time slots for given time range
    onFetchTimeSlots(listingId, start, end, timeZone);
  }
};

const handleMonthClick = (
  currentMonth,
  monthlyTimeSlots,
  fetchMonthData,
  dayCountAvailableForBooking,
  timeZone,
  listingId,
  onFetchTimeSlots
) => monthFn => {
  // Callback function after month has been updated.
  // react-dates component has next and previous months ready (but inivisible).
  // we try to populate those invisible months before user advances there.
  fetchMonthData(
    monthFn(currentMonth, timeZone, 2),
    listingId,
    dayCountAvailableForBooking,
    timeZone,
    onFetchTimeSlots
  );

  // If previous fetch for month data failed, try again.
  const monthId = monthIdString(currentMonth, timeZone);
  const currentMonthData = monthlyTimeSlots[monthId];
  if (currentMonthData && currentMonthData.fetchTimeSlotsError) {
    fetchMonthData(
      currentMonth,
      listingId,
      dayCountAvailableForBooking,
      timeZone,
      onFetchTimeSlots
    );
  }
};

// In case start or end date for the booking is missing
// focus on that input, otherwise continue with the
// default handleSubmit function.
const handleFormSubmit = (
  setFocusedInput,
  onSubmit,
  seatsChanged,
  guestsValue,
  singleBedroomSelected
) => e => {
  const { startDate, endDate } = e.bookingDates || {};
  if (!startDate) {
    e.preventDefault();
    setFocusedInput(START_DATE);
  } else if (!endDate) {
    e.preventDefault();
    setFocusedInput(END_DATE);
  } else {
    let data = { ...e, guests: guestsValue.toString() };
    data.seats = seatsChanged;
    // Reaches orderData via ListingPage.shared.js's `...otherOrderData` passthrough.
    data.singleBedroom = !!singleBedroomSelected;
    onSubmit(data);
  }
};

// Function that can be passed to nested components
// so that they can notify this component when the
// focused input changes.
const handleFocusedInputChange = setFocusedInput => focusedInput => {
  setFocusedInput(focusedInput);
};

// When the values of the form are updated we need to fetch
// lineItems from this Template's backend for the EstimatedTransactionMaybe
// In case you add more fields to the form, make sure you add
// the values here to the orderData object.
const handleFormSpyChange = (
  listingId,
  isOwnListing,
  fetchLineItemsInProgress,
  onFetchTransactionLineItems,
  triggerDataElbAction,
  seatsChanged,
  guestsValue,
  setDates
) => formValues => {
  const { startDate, endDate } =
    formValues.values && formValues.values.bookingDates ? formValues.values.bookingDates : {};
  const seats = formValues.values && formValues.values.seats ? formValues.values.seats : 1;
  const guests = formValues.values && formValues.values.guests ? formValues.values.guests : guestsValue;

  if (seatsChanged != Number(seats)) {
    triggerDataElbAction(seats);
  }
  if (startDate && endDate && !fetchLineItemsInProgress) {
    onFetchTransactionLineItems({
      orderData: {
        bookingStart: startDate,
        bookingEnd: endDate,
        seats: Number(seats),
        guests: Number(guests) || 1,
      },
      listingId,
      isOwnListing,
    });
  }
};

// IconArrowHead component might not be defined if exposed directly to the file.
// This component is called before IconArrowHead component in components/index.js
const PrevIcon = props => (
  <IconArrowHead {...props} direction="left" rootClassName={css.arrowIcon} />
);
const NextIcon = props => (
  <IconArrowHead {...props} direction="right" rootClassName={css.arrowIcon} />
);

const Next = props => {
  const { currentMonth, dayCountAvailableForBooking, timeZone } = props;
  const nextMonthDate = nextMonthFn(currentMonth, timeZone);

  return isDateSameOrAfter(
    nextMonthDate,
    endOfRange(TODAY, dayCountAvailableForBooking, timeZone)
  ) ? null : (
    <NextIcon />
  );
};

const Prev = props => {
  const { currentMonth, timeZone } = props;
  const prevMonthDate = prevMonthFn(currentMonth, timeZone);
  const currentMonthDate = getStartOf(TODAY, 'month', timeZone);

  return isDateSameOrAfter(prevMonthDate, currentMonthDate) ? <PrevIcon /> : null;
};

/**
 * Custom guests row styled exactly like the new design:
 * [LABEL]           [▾]
 * [value text]
 *
 * A native <select> is overlaid (opacity:0, absolute) so the native dropdown opens on click.
 */
const GuestsSelectRow = ({ label, displayValue, value, onChange, options, dataElbaction }) => (
  <div className={css.guestsSelectRow} data-elbaction={dataElbaction}>
    <div className={css.guestsSelectDisplay}>
      <div className={css.guestsSelectLabel}>{label}</div>
      <div className={css.guestsSelectValue}>{displayValue}</div>
    </div>
    <span className={css.guestsSelectArrow} aria-hidden="true">▾</span>
    <select
      className={css.guestsSelectNative}
      value={value}
      onChange={onChange}
      aria-label={label}
    >
      {options.map((opt, i) => (
        <option key={i} value={opt.value !== undefined ? opt.value : opt}>
          {opt.label !== undefined ? opt.label : opt}
        </option>
      ))}
    </select>
  </div>
);

// Keep legacy components for non-experience type fallback
const SeatsDropdown = ({ label, value, onChange, options }) => (
  <div className={css.fieldSelect}>
    <label className={css.fieldLabel}>{label}</label>
    <div className={css.selectWrapper}>
      <div className={css.arrowItem}> </div>
      <select
        name="seats"
        id="seats"
        className={css.fieldSelectOptions}
        onChange={onChange}
        value={value}
        data-elbaction="click:click selectRooms"
      >
        {options.map((person, index) => (
          <option key={index} value={person}>
            {person}
          </option>
        ))}
      </select>
    </div>
  </div>
);

const GuestsControl = ({ label, value, onDecrease, onIncrease }) => (
  <div className={css.guestsFieldWrapper}>
    <label className={css.fieldLabel}>{label}</label>
    <div className={css.guestsField} data-elbaction="click:click selectGuests">
      <button className={css.minusButton} onClick={onDecrease}>
        -
      </button>
      <input name="guests" id="guests" className={css.fieldGuestInput} value={value} readOnly />
      <button className={css.plusButton} onClick={onIncrease}>
        +
      </button>
    </div>
  </div>
);

export const BookingDatesFormComponent = props => {
  const [focusedInput, setFocusedInput] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(getStartOf(TODAY, 'month', props.timeZone));
  const seatsDivRef = useRef(null);
  const [elbSeatsData, setElbSeatsData] = useState({});
  // PERFORMANCE FIX: Use refs instead of state for date tracking
  // This prevents unnecessary re-renders when dates change
  const startingDateRef = useRef(null);
  const endingDateRef = useRef(null);
  const _listingId = props.listingId.uuid;

  // Helper function to safely access localStorage
  const getLocalStorageItem = key => {
    if (typeof window !== 'undefined' && window.localStorage) {
      return localStorage.getItem(key);
    }
    return null;
  };

  const setLocalStorageItem = (key, value) => {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem(key, value);
    }
  };

  const [seatsChanged, setSeatsChanged] = useState(() => {
    const savedSeats = getLocalStorageItem(`selectedSeats_${_listingId}`);
    return savedSeats ? parseInt(savedSeats, 10) : 1;
  });

  const [guestsValue, setGuestsValue] = useState(() => {
    const savedGuests = getLocalStorageItem(`selectedGuests_${_listingId}`);
    return savedGuests ? parseInt(savedGuests, 10) : 1;
  });

  // Bedroom choice for experiences whose host offers single bedrooms. Double is the default and
  // prices exactly as before; single prices the booking from the host's per-guest single-bedroom
  // amount instead. Kept in localStorage under the same convention as seats/guests because
  // CheckoutPage re-reads the choice from there rather than receiving it through navigation state.
  const [singleBedroom, setSingleBedroom] = useState(() => {
    return getLocalStorageItem(`selectedSingleBedroom_${_listingId}`) === 'true';
  });

  const [notification, setNotification] = useState(null);
  const triggerDataElbAction = seats => {
    let newElbSeats = { ['data-elbaction']: `click:click selectbedamt-[${seats}]` };
    setElbSeatsData(newElbSeats);
  };

  useEffect(() => {
    const divElement = seatsDivRef.current;
    if (divElement) {
      const event = new MouseEvent('click', { bubbles: true });
      divElement.dispatchEvent(event);
    }
  }, [elbSeatsData]);

  useEffect(() => {
    // Call onMonthChanged function if it has been passed in among props.
    if (props.onMonthChanged) {
      props.onMonthChanged(monthId);
    }
  }, [currentMonth]);

  useEffect(() => {
    onFormSpyChange({
      values: {
        seats: seatsChanged,
        guests: guestsValue,
        bookingDates: { startDate: startingDateRef.current, endDate: endingDateRef.current },
      },
    });
  }, [seatsChanged, guestsValue]);

  useEffect(() => {
    setTimeout(() => {
      onFormSpyChange({
        values: {
          seats: seatsChanged,
          guests: guestsValue,
          bookingDates: { startDate: startingDateRef.current, endDate: endingDateRef.current },
        },
      });
    }, 100);
  }, []);

  const {
    rootClassName,
    className,
    price: unitPrice,
    listingId,
    currentListing,
    isOwnListing,
    fetchLineItemsInProgress,
    onFetchTransactionLineItems,
    onSubmit,
    timeZone,
    marketplaceName,
    showPriceDetails,
    bookingDisabled,
    showBookingNoAvailableError,
    ...rest
  } = props;

  const dayCountAvailableForBooking = 365;

  const classes = classNames(rootClassName || css.root, className);

  const isExperienceType =
    currentListing.attributes?.publicData?.listingType == 'impact-experience';

  // The bedroom dropdown only exists when this experience's host offers single bedrooms. The
  // price is re-checked (not just the flag) so a half-written listing can't render a dropdown
  // that changes nothing.
  const singleBedroomAvailable =
    isExperienceType &&
    !!currentListing.attributes?.publicData?.singleBedroomAvailable &&
    !!currentListing.attributes?.publicData?.singleBedroomPrice?.amount;

  // Never trust the stored choice on its own: if the host withdraws single bedrooms after a
  // customer picked one, the stale localStorage flag must not keep pricing the booking from it.
  const singleBedroomSelected = singleBedroomAvailable && singleBedroom;

  const onFormSubmit = handleFormSubmit(
    setFocusedInput,
    onSubmit,
    seatsChanged,
    guestsValue,
    singleBedroomSelected
  );
  const onFocusedInputChange = handleFocusedInputChange(setFocusedInput);
  const onFormSpyChange = handleFormSpyChange(
    listingId,
    isOwnListing,
    fetchLineItemsInProgress,
    onFetchTransactionLineItems,
    triggerDataElbAction,
    seatsChanged,
    guestsValue
  );

  const fixedStartingDay = currentListing?.attributes?.publicData?.fixedStartingDay ?? 'any';

  const intl = useIntl();

  // A locale-keyed no-op, form-level validator. Its identity changes whenever the
  // language changes, which makes react-final-form call form.setConfig('validate'),
  // re-running ALL field-level validators against the current locale. Without this,
  // final-form never re-validates on a language switch, so an already-shown message
  // (e.g. "Select an available date") stays frozen in the language it was first
  // rendered in, while the rest of the panel updates.
  const revalidateOnLocaleChange = useMemo(() => () => undefined, [intl.locale]);

  // The estimated price depends on the guest count (rooms/beds are derived from
  // it on the server), so every guests change must re-run the form spy to refetch
  // line items.
  const notifyGuestsOrSeatsChanged = (seats, guests) => {
    onFormSpyChange({
      values: {
        seats,
        guests,
        bookingDates: { startDate: startingDateRef.current, endDate: endingDateRef.current },
      },
    });
  };

  const maxStayUnits = () => {
    const publicData = currentListing?.attributes?.publicData;
    const isDorm = publicData?.roomtype === 'shared_bedroom';
    return (
      Number((isDorm ? publicData?.bedamount : publicData?.roomamount) || publicData?.bedamount) ||
      1
    );
  };

  const increaseGuestsValue = e => {
    e.preventDefault();
    let peopleAmount = currentListing?.attributes?.publicData?.peopleAmount
      ? currentListing?.attributes?.publicData?.peopleAmount
      : 1;
    let totalGuestsAllowed = Number(peopleAmount) * Number(seatsChanged);
    if (totalGuestsAllowed > guestsValue) {
      const newGuestsValue = guestsValue + 1;
      setGuestsValue(newGuestsValue);
      setLocalStorageItem(`selectedGuests_${_listingId}`, newGuestsValue);
      notifyGuestsOrSeatsChanged(seatsChanged, newGuestsValue);
    } else if (Number(seatsChanged) < maxStayUnits()) {
      // The party no longer fits the selected rooms/beds — add one automatically
      // instead of asking the guest to work that out.
      const newSeatsValue = Number(seatsChanged) + 1;
      const newGuestsValue = guestsValue + 1;
      setSeatsChanged(newSeatsValue);
      setGuestsValue(newGuestsValue);
      setLocalStorageItem(`selectedSeats_${_listingId}`, newSeatsValue);
      setLocalStorageItem(`selectedGuests_${_listingId}`, newGuestsValue);
      notifyGuestsOrSeatsChanged(newSeatsValue, newGuestsValue);
    } else {
      // Store the translation id (not the formatted string) so the notification
      // re-translates when the language is switched while it is still visible.
      setNotification('guestsWarning.notAllowed');
    }
  };
  const decreaseGuestsValue = e => {
    e.preventDefault();
    if (guestsValue > 1) {
      const newGuestsValue = guestsValue - 1;
      setGuestsValue(newGuestsValue);
      setLocalStorageItem(`selectedGuests_${_listingId}`, newGuestsValue);
      notifyGuestsOrSeatsChanged(seatsChanged, newGuestsValue);
    }
  };
  const handleSeatsChanged = e => {
    const newSeats = Number(e.target.value);

    let peopleAmount = currentListing?.attributes?.publicData?.peopleAmount
      ? currentListing?.attributes?.publicData?.peopleAmount
      : 1;
    let updatedGuestsValue = guestsValue;
    if (guestsValue > Number(e.target.value) * Number(peopleAmount)) {
      const newGuestsValue = Number(e.target.value) * Number(peopleAmount);
      updatedGuestsValue = newGuestsValue;
      setGuestsValue(newGuestsValue);
      setLocalStorageItem(`selectedGuests_${_listingId}`, newGuestsValue);
    }
    const newSeatsValue = e.target.value;
    setSeatsChanged(newSeatsValue);
    setLocalStorageItem(`selectedSeats_${_listingId}`, newSeatsValue);
    onFormSpyChange({
      values: {
        seats: newSeatsValue,
        guests: updatedGuestsValue,
        bookingDates: { startDate: startingDateRef.current, endDate: endingDateRef.current },
      },
    });
  };

  const handleMDESeatsChanged = e => {
    const newSeatsValue = Number(e.target.value);
    setSeatsChanged(newSeatsValue);
    setLocalStorageItem(`selectedSeats_${_listingId}`, newSeatsValue);

    onFormSpyChange({
      values: {
        seats: newSeatsValue,
        bookingDates: { startDate: startingDateRef.current, endDate: endingDateRef.current },
      },
    });
  };
  const handleSingleBedroomChanged = e => {
    const newValue = e.target.value === 'single';
    setSingleBedroom(newValue);
    setLocalStorageItem(`selectedSingleBedroom_${_listingId}`, String(newValue));
  };

  // Use centralized price calculation utility
  const availablePeopleMDE = createPeoplePriceArray(currentListing?.attributes) ?? [];

  const calculateDaysBetweenDates = breakdown => {
    const startDate = new Date(breakdown.startDate);
    const endDate = new Date(breakdown.endDate);
    const timeDifference = endDate.getTime() - startDate.getTime();
    const daysDifference = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
    return daysDifference;
  };
  return (
    <FinalForm
      {...rest}
      unitPrice={unitPrice}
      onSubmit={onFormSubmit}
      validate={revalidateOnLocaleChange}
      render={fieldRenderProps => {
        const {
          endDatePlaceholder,
          startDatePlaceholder,
          formId,
          handleSubmit,
          intl,
          lineItemUnitType,
          values,
          monthlyTimeSlots,
          lineItems,
          fetchLineItemsError,
          onFetchTimeSlots,
          bedamount,
        } = fieldRenderProps;
        const { startDate, endDate } = values && values.bookingDates ? values.bookingDates : {};

        const startDateErrorMessage = intl.formatMessage({
          id: 'FieldDateRangeInput.invalidStartDate',
        });
        const endDateErrorMessage = intl.formatMessage({
          id: 'FieldDateRangeInput.invalidEndDate',
        });

        // This is the place to collect breakdown estimation data.
        // Note: lineItems are calculated and fetched from this Template's backend
        // so we need to pass only booking data that is needed otherwise
        // If you have added new fields to the form that will affect to pricing,
        // you need to add the values to handleOnChange function
        const breakdownData =
          startDate && endDate
            ? {
                startDate,
                endDate,
              }
            : null;
        const numberOfNightsSelected = breakdownData
          ? calculateDaysBetweenDates(breakdownData)
          : null;
        const showEstimatedBreakdown =
          breakdownData && lineItems && !fetchLineItemsInProgress && !fetchLineItemsError;

        const dateFormatOptions = {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        };
        const minimumBookableNights = currentListing?.attributes?.publicData?.minimumBookableNights
          ? currentListing?.attributes?.publicData?.minimumBookableNights
          : null;
        const minimumBookableNightsInvalid =
          breakdownData && numberOfNightsSelected && minimumBookableNights
            ? Number(numberOfNightsSelected) < minimumBookableNights
              ? true
              : false
            : false;
        const startOfToday = getStartOf(TODAY, 'day', timeZone);
        const packageDays =
          isExperienceType && currentListing.attributes?.publicData?.MDE
            ? extractDaysAndNights(currentListing.attributes.publicData.MDE)?.days
            : null;
        const endDatePlaceholderDate = packageDays
          ? addTime(startOfToday, packageDays - 1, 'days', timeZone)
          : addTime(startOfToday, 1, 'days', timeZone);
        const startDatePlaceholderText =
          startDatePlaceholder || intl.formatDate(startOfToday, dateFormatOptions);
        const endDatePlaceholderText =
          endDatePlaceholder || intl.formatDate(endDatePlaceholderDate, dateFormatOptions);

        const onMonthClick = handleMonthClick(
          currentMonth,
          monthlyTimeSlots,
          fetchMonthData,
          dayCountAvailableForBooking,
          timeZone,
          listingId,
          onFetchTimeSlots
        );
        const isDayBlocked = isDayBlockedFn(
          monthlyTimeSlots,
          startDate,
          endDate,
          lineItemUnitType,
          dayCountAvailableForBooking,
          timeZone,
          fixedStartingDay
        );
        const isOutsideRange = isOutsideRangeFn(
          monthlyTimeSlots,
          startDate,
          endDate,
          lineItemUnitType,
          dayCountAvailableForBooking,
          timeZone
        );
        const availableDates = getBookableDays(
          monthlyTimeSlots,
          dayCountAvailableForBooking,
          fixedStartingDay
        );

        // Seasonal (date-range) pricing notice: when the chosen check-in date falls inside a
        // host-configured range, the booking is charged that range's price instead of the
        // standard price shown in the panel header. Surface it here so the higher/lower total
        // below isn't a surprise. Stays override the per-night price; experiences override the
        // flat per-guest price for the selected guest-count. Matched the same way it is charged.
        const seasonalPublicData = currentListing?.attributes?.publicData;
        let seasonalNotice = null;
        if (startDate && unitPrice) {
          if (isExperienceType) {
            const seasonalAmount = getSeasonalPriceForSeats(
              seasonalPublicData?.seasonalPricing,
              startDate,
              seatsChanged
            );
            if (seasonalAmount != null) {
              const defaultForSeats = (
                createPeoplePriceArray(currentListing?.attributes) || []
              ).find(p => Number(p.guests) === Number(seatsChanged));
              const standardAmount = defaultForSeats?.value;
              if (standardAmount != null && seasonalAmount !== standardAmount) {
                seasonalNotice = {
                  isExperience: true,
                  seasonal: new Money(seasonalAmount, unitPrice.currency),
                  standard: new Money(standardAmount, unitPrice.currency),
                };
              }
            }
          } else {
            const range = getSeasonalRangeForStart(seasonalPublicData?.seasonalPricing, startDate);
            if (range && range.amount !== unitPrice.amount) {
              seasonalNotice = {
                isExperience: false,
                seasonal: new Money(range.amount, unitPrice.currency),
                standard: unitPrice,
              };
            }
          }
        }

        // Stays: the selector counts rooms (beds for dormitories), so offer up to the
        // listing's room/bed stock. Per-guest price tiers (price2P…) only gate the
        // experience guest dropdown, never a stay's rooms.
        const projectRoomtypeRaw =
          seasonalPublicData?.roomtype || currentListing?.attributes?.title.split(' • ')[1];
        const seatsSelection = isExperienceType
          ? getAvailableSeatsWithPrices(currentListing?.attributes) || [1]
          : Array.from({ length: maxStayUnits() }, (v, k) => k + 1);

        const seatsLabel =
          projectRoomtypeRaw == 'shared_bedroom'
            ? intl.formatMessage({ id: 'bookingUnits.beds' })
            : intl.formatMessage({ id: 'bookingUnits.rooms' });
        const guestsLabel = intl.formatMessage({ id: 'bookingpanel.guestsLabel' });

        return (
          <Form onSubmit={handleSubmit} className={classes} enforcePagePreloadFor="CheckoutPage">
            <FormSpy
              subscription={{ values: true }}
              onChange={data => {
                // PERFORMANCE FIX: Use refs instead of state to avoid re-renders
                if (data?.values?.bookingDates?.endDate) {
                  endingDateRef.current = data?.values?.bookingDates?.endDate;
                }
                if (data?.values?.bookingDates?.startDate) {
                  startingDateRef.current = data?.values?.bookingDates?.startDate;
                }
                data.values.seats = seatsChanged;
                data.values.guests = guestsValue;
                onFormSpyChange(data);
              }}
            />

            {notification && (
              <Notification
                message={intl.formatMessage({ id: notification })}
                onClose={() => setNotification(null)}
              />
            )}

            <div className={css.datesGuestsBox}>
              <FieldDateRangeInput
              className={css.bookingDates}
              availableDates={availableDates}
              name="bookingDates"
              isDaily={lineItemUnitType === LINE_ITEM_DAY}
              startDateId={`${formId}.bookingStartDate`}
              startDateLabel={intl.formatMessage({
                id: 'BookingDatesForm.bookingStartTitle',
              })}
              startDatePlaceholderText={startDatePlaceholderText}
              endDateId={`${formId}.bookingEndDate`}
              endDateLabel={intl.formatMessage({
                id: 'BookingDatesForm.bookingEndTitle',
              })}
              endDatePlaceholderText={endDatePlaceholderText}
              focusedInput={focusedInput}
              onFocusedInputChange={onFocusedInputChange}
              format={v => {
                const { startDate, endDate } = v || {};
                // Format the Final Form field's value for the DateRangeInput
                // DateRangeInput operates on local time zone, but the form uses listing's time zone
                const formattedStart = startDate
                  ? timeOfDayFromTimeZoneToLocal(startDate, timeZone)
                  : startDate;
                const formattedEnd = endDate
                  ? timeOfDayFromTimeZoneToLocal(endDate, timeZone)
                  : endDate;
                return v ? { startDate: formattedStart, endDate: formattedEnd } : v;
              }}
              parse={v => {
                const { startDate, endDate } = v || {};
                // Parse the DateRangeInput's value (local noon) for the Final Form
                // The form expects listing's time zone and start of day aka 00:00
                const parsedStart = startDate
                  ? getStartOf(timeOfDayFromLocalToTimeZone(startDate, timeZone), 'day', timeZone)
                  : startDate;
                const parsedEnd = endDate
                  ? getStartOf(timeOfDayFromLocalToTimeZone(endDate, timeZone), 'day', timeZone)
                  : endDate;
                return v ? { startDate: parsedStart, endDate: parsedEnd } : v;
              }}
              useMobileMargins
              validate={composeValidators(
                required(
                  intl.formatMessage({
                    id: 'BookingDatesForm.requiredDate',
                  })
                ),
                bookingDatesRequired(startDateErrorMessage, endDateErrorMessage)
              )}
              initialVisibleMonth={initialVisibleMonth(startDate || startOfToday, timeZone)}
              navNext={
                <Next
                  currentMonth={currentMonth}
                  timeZone={timeZone}
                  dayCountAvailableForBooking={dayCountAvailableForBooking}
                />
              }
              navPrev={<Prev currentMonth={currentMonth} timeZone={timeZone} />}
              onPrevMonthClick={() => {
                setCurrentMonth(prevMonth => prevMonthFn(prevMonth, timeZone));
                onMonthClick(prevMonthFn);
              }}
              onNextMonthClick={() => {
                setCurrentMonth(prevMonth => nextMonthFn(prevMonth, timeZone));
                onMonthClick(nextMonthFn);
              }}
              isDayBlocked={isDayBlocked}
              isOutsideRange={isOutsideRange}
              isBlockedBetween={isBlockedBetween(monthlyTimeSlots, timeZone)}
              disabled={fetchLineItemsInProgress}
              onClose={event => {
                setCurrentMonth(getStartOf(event?.startDate ?? startOfToday, 'month', timeZone));
              }}
              seatsChanged={seatsChanged}
              isExperienceType={isExperienceType}
              autoIncreaseDays={
                currentListing.attributes?.publicData?.MDE
                  ? extractDaysAndNights(currentListing.attributes?.publicData?.MDE).days
                  : 0
              }
            />

              {/* Guests / Seats row inside the box */}
              {isExperienceType ? (
                <>
                  <GuestsSelectRow
                    label={intl.formatMessage({ id: 'listingPage.NumberofGuestsLabel' })}
                    displayValue={intl.formatMessage(
                      { id: seatsChanged == 1 ? 'bookingPanel.travelerSingular' : 'bookingPanel.travelerPlural' },
                      { count: seatsChanged }
                    )}
                    value={seatsChanged}
                    onChange={handleMDESeatsChanged}
                    options={availablePeopleMDE.map(p => ({ value: p.guests, label: p.guests }))}
                    dataElbaction="click:click selectGuests"
                  />
                  {singleBedroomAvailable && (
                    <GuestsSelectRow
                      label={intl.formatMessage({ id: 'BookingDatesForm.bedroomTypeLabel' })}
                      displayValue={intl.formatMessage({
                        id: singleBedroomSelected
                          ? 'BookingDatesForm.bedroomTypeSingle'
                          : 'BookingDatesForm.bedroomTypeDouble',
                      })}
                      value={singleBedroomSelected ? 'single' : 'double'}
                      onChange={handleSingleBedroomChanged}
                      options={[
                        {
                          value: 'double',
                          label: intl.formatMessage({ id: 'BookingDatesForm.bedroomTypeDouble' }),
                        },
                        {
                          value: 'single',
                          label: intl.formatMessage({ id: 'BookingDatesForm.bedroomTypeSingle' }),
                        },
                      ]}
                      dataElbaction="click:click selectBedroomType"
                    />
                  )}
                </>
              ) : (
                <div className={css.guestsRow}>
                  <div className={css.seatsAndGuestsWrapper}>
                    <SeatsDropdown
                      label={seatsLabel}
                      value={seatsChanged}
                      onChange={handleSeatsChanged}
                      options={seatsSelection}
                    />
                    <GuestsControl
                      label={guestsLabel}
                      value={guestsValue}
                      onDecrease={decreaseGuestsValue}
                      onIncrease={increaseGuestsValue}
                    />
                  </div>
                </div>
              )}
            </div>

            <div ref={seatsDivRef} {...elbSeatsData}></div>

            {seasonalNotice && (
              <div className={css.seasonalNotice}>
                <span className={css.seasonalNoticeIcon} aria-hidden="true">
                  i
                </span>
                <span className={css.seasonalNoticeText}>
                  <FormattedMessage
                    id={
                      seasonalNotice.isExperience
                        ? 'BookingDatesForm.seasonalPriceNoticeExperience'
                        : 'BookingDatesForm.seasonalPriceNotice'
                    }
                    values={{
                      seasonalPrice: (
                        <span className={css.seasonalNoticePrice}>
                          {formatMoney(intl, seasonalNotice.seasonal)}
                        </span>
                      ),
                      standardPrice: formatMoney(intl, seasonalNotice.standard),
                    }}
                  />
                </span>
              </div>
            )}

            {showEstimatedBreakdown && (
              <div className={css.priceBreakdownContainer}>
                <EstimatedCustomerBreakdownMaybe
                  breakdownData={breakdownData}
                  lineItems={lineItems}
                  timeZone={timeZone}
                  currency={unitPrice.currency}
                  marketplaceName={marketplaceName}
                  processName={BOOKING_PROCESS_NAME}
                  projectRoomtypeRaw={projectRoomtypeRaw}
                  currentListing={currentListing}
                  showPriceDetails={showPriceDetails}
                  seats={seatsChanged}
                  guests={guestsValue}
                  singleBedroom={singleBedroomSelected}
                />
              </div>
            )}
            {fetchLineItemsError && (
              <span className={css.sideBarError}>
                <FormattedMessage id="BookingDatesForm.fetchLineItemsError" />
              </span>
            )}
            {minimumBookableNightsInvalid && (
              <span className={css.sideBarError}>
                <FormattedMessage
                  id="BookingDatesForm.minimumBookableNightsError"
                  values={{ value: minimumBookableNights }}
                />
              </span>
            )}
            {showBookingNoAvailableError && (
              <span className={css.bookingDatesNotAvailableError}>
                <FormattedMessage id="bookingPanel.datesNotAvailable" />
              </span>
            )}
            <div
              className={css.submitButton}
              data-elb="general"
              data-elbaction="click:click bookbtn"
            >
              <PrimaryButton
                type="submit"
                inProgress={fetchLineItemsInProgress}
                className={css.primaryButtonHover}
                disabled={bookingDisabled || minimumBookableNightsInvalid}
                onClick={() => seedReservationTimer(30, 0)}
              >
                <FormattedMessage id="BookingDatesForm.requestToBook" />
              </PrimaryButton>
            </div>
          </Form>
        );
      }}
    />
  );
};

BookingDatesFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  price: null,
  isOwnListing: false,
  startDatePlaceholder: null,
  endDatePlaceholder: null,
  lineItems: null,
  fetchLineItemsError: null,
  monthlyTimeSlots: null,
};

BookingDatesFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  marketplaceName: string.isRequired,
  lineItemUnitType: propTypes.lineItemUnitType.isRequired,
  price: propTypes.money,
  isOwnListing: bool,
  monthlyTimeSlots: object,
  onFetchTimeSlots: func.isRequired,
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  intl: intlShape.isRequired,
  startDatePlaceholder: string,
  endDatePlaceholder: string,
  dayCountAvailableForBooking: number.isRequired,
  showPriceDetails: bool,
  bookingDisabled: bool,
  showBookingNoAvailableError: bool,
};

const BookingDatesForm = compose(injectIntl)(BookingDatesFormComponent);
BookingDatesForm.displayName = 'BookingDatesForm';

export default BookingDatesForm;
